import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, NavLink, Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { caseStudies } from './data/caseStudies.js';
import Seo from './components/Seo.jsx';
import PictureImg from './components/PictureImg.jsx';
import VimeoFacade from './components/VimeoFacade.jsx';

const CASE_STUDY_SEO = {
  fnzcci: {
    title: 'FNZCCI documentary | nuances',
    description: 'An eleven-minute documentary for the France New Zealand Chamber of Commerce and Industry. 2025.',
  },
  debtmanagers: {
    title: 'DebtManagers | nuances',
    description: 'A six-film series for DebtManagers. Five voices, one story. 2024.',
  },
};

const useDeferredMount = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Don't defer-mount under reduced motion — saves loading the chunk entirely
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Plain setTimeout (no requestIdleCallback). On iOS Safari, RIC's callback
    // could be delayed indefinitely with the timeout option not always honoured,
    // leaving heroAssetsReady false forever and the hero video never mounting.
    const id = setTimeout(() => setReady(true), 50);
    return () => clearTimeout(id);
  }, []);
  return ready;
};

const HERO_BG = 'radial-gradient(ellipse 40% 45% at 85% 18%,rgba(150,120,80,.11) 0%,transparent 52%),radial-gradient(ellipse 90% 65% at 50% 100%,rgba(8,6,4,.7) 0%,transparent 60%),linear-gradient(168deg,#0E0C0A,#141210)';
const GRAIN_OPACITY = 0.016;

const LOGOS = [
  { src: '/logos/vinci.svg', alt: 'VINCI' },
  { src: '/logos/fnzcci.png', alt: 'French New Zealand Chamber of Commerce' },
  { src: '/logos/debtmanagers.png', alt: 'DebtManagers' },
  { src: '/logos/bravegen.svg', alt: 'BraveGen' },
];

const ATTRIBUTION_NOTE = 'Historical work, produced while Claude was Co-Founder at MindShift Films.';

const Showreel = ({context}) => {
  const [playing, setPlaying] = useState(false);
  return (
    <section className={`showreel showreel-${context}`}>
      {!playing ? (
        <button className="showreel-poster" onClick={()=>setPlaying(true)} aria-label="Play showreel">
          <PictureImg src="/05-Video/showreel-poster.jpg" alt="" width={1920} height={1080} loading="lazy"/>
          <span className="showreel-play-indicator" aria-hidden="true">
            <svg viewBox="0 0 60 60" width="60" height="60">
              <circle cx="30" cy="30" r="29" fill="none" stroke="rgba(250,247,242,0.9)" strokeWidth="1.5"/>
              <path d="M 24 18 L 24 42 L 44 30 Z" fill="rgba(250,247,242,0.95)"/>
            </svg>
            <span className="showreel-label">Watch the showreel</span>
          </span>
        </button>
      ) : (
        <div className="showreel-player">
          <iframe
            src="https://player.vimeo.com/video/1186621971?dnt=1&color=2D2D2D&title=0&byline=0&portrait=0&autoplay=1"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Showreel"
          />
        </div>
      )}
    </section>
  );
};

const LogoStrip = () => (
  <section className="logo-strip" aria-label="Selected clients">
    <div className="logo-strip-track">
      {[...LOGOS, ...LOGOS].map((logo, i) => (
        <div key={i} className="logo-strip-item">
          <img src={logo.src} alt={logo.alt} loading="lazy" />
          {logo.subcaption && <span className="logo-strip-subcaption">{logo.subcaption}</span>}
        </div>
      ))}
    </div>
    <p className="logo-strip-note">{ATTRIBUTION_NOTE}</p>
  </section>
);

const WORKS = [
  {
    id: 'vinci-go-north',
    title: 'Go North Corridor',
    client: 'VINCI',
    year: 2025,
    role: 'Director',
    cat: 'corporate',
    span: 'wide',
    poster: '/05-Video/work/vinci-go-north-poster.avif',
    hoverLoop: '/05-Video/work/vinci-go-north-loop.mp4',
    vimeoId: '1186636414',
    context: "A film made for the Go>North Consortium's tender for the Warkworth to Te Hana expressway. Internal-facing, values-led, built around the idea that the people who would build the project mattered as much as the project itself.",
  },
  {
    id: 'vinci-company-overview',
    title: 'Company Overview',
    client: 'VINCI',
    year: 2025,
    role: 'Director',
    cat: 'corporate',
    span: 'normal',
    poster: '/05-Video/work/vinci-company-overview-poster.avif',
    hoverLoop: '/05-Video/work/vinci-company-overview-loop.mp4',
    vimeoId: '1186623319',
    context: "A decade of VINCI in New Zealand, told through the people who lived it. We worked through their seven shared values and let 'Together' carry the film, focusing on civic engagement, sustainable careers, and the long view of building.",
  },
  {
    id: 'fnzcci-documentary',
    title: 'Documentary',
    client: 'FNZCCI',
    year: 2025,
    role: 'Director',
    cat: 'documentary',
    span: 'normal',
    poster: '/05-Video/work/fnzcci-documentary-poster.avif',
    hoverLoop: '/05-Video/work/fnzcci-documentary-loop.mp4',
    vimeoId: '1186636887',
    context: "Forty years of the French New Zealand Chamber of Commerce, captured as an 11 minute heritage piece for the Chamber's anniversary. Founding members and current leadership, archival materials interleaved with contemporary filming, one continuous thread through four decades.",
  },
  {
    id: 'debt-managers-company-overview',
    title: 'Company Overview',
    client: 'DebtManagers',
    year: 2024,
    role: 'Producer',
    cat: 'corporate',
    span: 'wide',
    poster: '/05-Video/work/debt-managers-company-overview-poster.avif',
    hoverLoop: '/05-Video/work/debt-managers-company-overview-loop.mp4',
    vimeoId: '1186656153',
    context: "A scripted overview film made with DebtManagers' own staff on camera. The brief asked for professional without being polished, so we built it around real people speaking plainly about how fairness, trust, and respect actually look in their work.",
  },
  {
    id: 'debt-managers-testimonials',
    title: 'Voices of DebtManagers',
    client: 'DebtManagers',
    year: 2024,
    role: 'Producer',
    cat: 'testimonial',
    span: 'normal',
    poster: '/05-Video/work/debt-managers-testimonials-poster.avif',
    hoverLoop: '/05-Video/work/debt-managers-testimonials-loop.mp4',
    vimeoId: '1186657150',
    isSeries: false,
    series: [
      { name: 'Mark', vimeoId: '1186656415' },
      { name: 'Shana', vimeoId: '1186656867' },
      { name: 'Nina', vimeoId: '1186656729' },
      { name: 'Tai', vimeoId: '1186657150' },
    ],
    testimonial: {
      quote: "Claude and the crew blew us away. It was our first time using video, and they got us, how different we are, and the real people behind our compassionate debt management process.",
      attribution: 'Mark Francis, General Manager, DebtManagers',
    },
    context: "A four-part series built around a single message: debt is something you manage, not something to be ashamed of. We sat down with four people whose stories carried that weight, and let them speak plainly about what changes when you pick up the phone. Part of a five-film series of customer testimonials. The full case study covers all five.",
  },
];

const canHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;

const buildVimeoEmbedURL = (vimeoId, {autoplay = true} = {}) => {
  const params = new URLSearchParams({dnt:'1',color:'2D2D2D',title:'0',byline:'0',portrait:'0',autoplay: autoplay ? '1' : '0'});
  return `https://player.vimeo.com/video/${vimeoId}?${params.toString()}`;
};

const SeriesModalMobile = ({work}) => {
  const [activeId, setActiveId] = useState(work.series[0].vimeoId);
  return (
    <div className="series-modal series-modal-mobile">
      <iframe
        key={activeId}
        src={buildVimeoEmbedURL(activeId, {autoplay: false})}
        title={work.title}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        className="work-modal-video"
      />
      <div className="series-tabs">
        {work.series.map((entry) => (
          <button
            key={entry.vimeoId}
            className={`series-tab ${activeId === entry.vimeoId ? 'active' : ''}`}
            onClick={() => setActiveId(entry.vimeoId)}>
            {entry.name}
          </button>
        ))}
      </div>
    </div>
  );
};

const SeriesPlayer = ({work}) => {
  const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;
  if (!isDesktop) return <SeriesModalMobile work={work}/>;
  return (
    <div className="series-modal series-modal-desktop">
      {work.series.map((entry, i) => (
        <div className="series-entry" key={entry.vimeoId}>
          <h4 className="series-name">{entry.name}</h4>
          <iframe
            src={buildVimeoEmbedURL(entry.vimeoId, {autoplay: false})}
            title={`${work.title}, ${entry.name}`}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            loading={i === 0 ? 'eager' : 'lazy'}
            className="work-modal-video"
          />
        </div>
      ))}
    </div>
  );
};

const Tile = ({p, cls, onClick, isOpen, onOpen, onClose, caseStudyBadge, caseStudyCtaSlug}) => {
  const [hov,setHov] = useState(false);
  const [closing,setClosing] = useState(false);
  const videoRef = useRef(null);
  const tileRef = useRef(null);
  const closeButtonRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const lockedScrollYRef = useRef(0);
  const navigate = useNavigate();
  const open = onClick || onOpen;

  const triggerClose = useCallback((after) => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      if (onClose) onClose();
      if (typeof after === 'function') after();
    }, 320);
  }, [onClose]);

  const handleCaseStudyCta = () => {
    triggerClose(() => navigate(`/work/${caseStudyCtaSlug}`));
  };

  useEffect(()=>{
    if(!canHover || !videoRef.current) return;
    if(hov){
      videoRef.current.play().catch(()=>{});
    }else{
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  },[hov]);

  // Escape closes via the animation path
  useEffect(()=>{
    if(!isOpen) return;
    const onKey = e => { if(e.key === 'Escape') triggerClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  },[isOpen, triggerClose]);

  // Body scroll lock while modal is open. Position:fixed + negative top trick.
  // scrollY is stored in a ref so it survives any re-render. The restore uses
  // behavior:'instant' to bypass html{scroll-behavior:smooth} in index.css,
  // which was animating the restore from 0 -> savedY (the visible jump).
  // Scrollbar width is compensated as padding-right so content doesn't shift
  // sideways on platforms with classic scrollbars.
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    lockedScrollYRef.current = scrollY;
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;
    return () => {
      const y = lockedScrollYRef.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.paddingRight = '';
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    };
  }, [isOpen]);

  // Focus management: capture before open, focus close button on open, restore on close
  useEffect(() => {
    if (isOpen) {
      lastFocusedRef.current = document.activeElement;
      const t = setTimeout(() => closeButtonRef.current?.focus(), 80);
      return () => clearTimeout(t);
    } else if (lastFocusedRef.current) {
      const target = lastFocusedRef.current;
      lastFocusedRef.current = null;
      if (typeof target.focus === 'function') target.focus({ preventScroll: true });
    }
  }, [isOpen]);

  const tileBg = p.poster ? { background: `url(${p.poster}) center/cover` } : null;
  const iframeTitle = p.client && p.client !== p.title ? `${p.title}, ${p.client}` : p.title;

  return (
    <>
      <div ref={tileRef} className={`tile ${cls||''}`} data-id={p.id} style={tileBg} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} onClick={open} role="button" tabIndex={0} onKeyDown={e=>e.key==='Enter'&&open()} aria-label={p.title}>
        {canHover && p.hoverLoop && (
          <video ref={videoRef} className="work-tile-loop" muted loop playsInline preload="none" poster={p.poster} aria-hidden="true">
            <source src={p.hoverLoop} type="video/mp4"/>
          </video>
        )}
        <div className={`tile-scan${hov?' on':''}`}/>
        <div className={`tile-ov${hov?' hov':''}`}>
          <div className="tile-meta"><span className="tile-cat">{p.cat}</span><span className="tile-yr">{p.year}</span></div>
          <div>
            <h2 className="tile-h3">{p.title}</h2>
            <p className={`tile-desc${hov?' vis':''}`}>{p.context}</p>
          </div>
        </div>
        {caseStudyBadge && <span className="cs-badge">Case study</span>}
      </div>
      {isOpen && createPortal(
        <div className={`modal-back${closing?' closing':''}`} onClick={()=>triggerClose()}>
          <button
            ref={closeButtonRef}
            className="modal-close"
            onClick={(e)=>{e.stopPropagation(); triggerClose();}}
            aria-label="Close"
          >&#x2715;</button>
          <div className={`modal-panel${closing?' closing':''}`} onClick={e=>e.stopPropagation()}>
            {p.isSeries ? (
              <>
                <div className="modal-content">
                  <div className="modal-meta"><span className="tile-cat">{p.cat}</span><span className="tile-yr">{p.year}</span><span className="modal-role">{p.role}</span></div>
                  <h2 className="modal-title">{p.title}</h2>
                  <p className="modal-description">{p.context}</p>
                  {p.testimonial && (
                    <blockquote className="work-testimonial">
                      <p className="work-testimonial-quote">{p.testimonial.quote}</p>
                      <cite className="work-testimonial-attribution">{p.testimonial.attribution}</cite>
                    </blockquote>
                  )}
                </div>
                <SeriesPlayer work={p}/>
              </>
            ) : (
              <>
                <div className="modal-video">
                  <iframe src={buildVimeoEmbedURL(p.vimeoId)} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen loading="lazy" title={iframeTitle}/>
                </div>
                <div className="modal-content">
                  <div className="modal-meta"><span className="tile-cat">{p.cat}</span><span className="tile-yr">{p.year}</span><span className="modal-role">{p.role}</span></div>
                  <h2 className="modal-title">{p.title}</h2>
                  <p className="modal-description">{p.context}</p>
                  {caseStudyCtaSlug && (
                    <div className="modal-cta-wrap">
                      <div className="modal-cta-divider" aria-hidden="true"/>
                      <button type="button" className="modal-cta" onClick={handleCaseStudyCta}>
                        Open the case study
                        <span className="modal-cta-arrow" aria-hidden="true">&rarr;</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const Nav = ({displayedPath}) => {
  const isHome = displayedPath === '/';
  const isCaseStudy = displayedPath.startsWith('/work/') && displayedPath !== '/work';
  const watchScroll = isHome || isCaseStudy;
  const [overDark, setOverDark] = useState(watchScroll);
  useEffect(() => {
    if (!watchScroll) {
      setOverDark(false);
      return;
    }
    const handleScroll = () => {
      const target = document.querySelector(isCaseStudy ? '.cs-hero' : '.statement');
      const navHeight = 80;
      if (target) {
        const rect = target.getBoundingClientRect();
        setOverDark(rect.bottom > navHeight);
      } else {
        setOverDark(window.scrollY < window.innerHeight * 0.8);
      }
    };
    const t = setTimeout(handleScroll, 60);
    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => { clearTimeout(t); window.removeEventListener('scroll', handleScroll); };
  }, [displayedPath, watchScroll, isCaseStudy]);
  const links = [
    {to:'/work', label:'Work'},
    {to:'/services', label:'Services'},
    {to:'/about', label:'About'},
    {to:'/contact', label:'Contact'},
  ];
  return (
    <header className={`site-nav ${overDark ? 'nav-over-dark' : 'nav-over-light'}`}>
      <Link to="/" className="nav-wm">nuances</Link>
      <nav className="nav-links">
        {links.map(l=>(
          <NavLink key={l.to} to={l.to} className={({isActive})=>`nav-link${isActive?' active':''}`}>
            {l.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
};

const Footer = () => (
  <footer className="footer">
    <div className="footer-mark">nuances</div>
    <p className="footer-tagline">A subtle accent.</p>
    <a className="footer-email" href="mailto:claude@nuances.video">claude@nuances.video</a>
    <p className="footer-meta">Auckland, New Zealand</p>
    <p className="footer-copyright">&#169; 2026 Nuances Productions Limited</p>
  </footer>
);

const Statement = () => (
  <section className="statement">
    <div className="statement-inner">
      <p>Most communication fails quietly. Not at the brief, not in the edit, not on delivery. It fails in the third row, where someone has already decided this is not for them.</p>
      <p>We make video for that person. The one who needs a reason to stay. The work begins long before the camera, with a single question: what does this person actually need to feel, to move?</p>
    </div>
  </section>
);

const Definition = () => (
  <section className="def">
    <div className="def-inner">
      <div className="def-hd">
        <span className="def-word">nuance</span>
        <span className="def-pho">/ njuːˈɑːns /</span>
        <span className="def-pos">noun</span>
      </div>
      <p className="def-meaning">A subtle difference in or shade of meaning, expression, or sound. The small detail that transforms something ordinary into something unforgettable.</p>
      <p className="def-body">The pause before a key message. The arc that earns trust. The word that lands where another wouldn't. The difference between a video that gets watched and one that gets remembered.</p>
    </div>
  </section>
);

const LeadMagnet = () => {
  const [email,setEmail] = useState('');
  const [state,setState] = useState('idle');
  const handleSubmit = async e => {
    e.preventDefault();
    if(!email.includes('@')) return;
    setState('submitting');
    try {
      const fd = new FormData(e.target);
      const r = await fetch('https://formspree.io/f/xbdwvayo', {method:'POST', body:fd, headers:{Accept:'application/json'}});
      if(r.ok){
        setState('success');
        setEmail('');
      } else {
        setState('error');
      }
    } catch(err){
      setState('error');
    }
  };
  const busy = state==='submitting';
  return (
    <section className="lm" id="leadmagnet">
      <div className="lm-inner">
        {state==='success' ? (
          <p className="lm-ty">Thanks. The template is on its way.</p>
        ) : (
          <>
            <h2 className="lm-h">The Video Brief Template</h2>
            <p className="lm-p">The same framework we use with clients. Questions that move a brief from "we need a video" to "we need this one."</p>
            <form action="https://formspree.io/f/xbdwvayo" method="POST" className="lm-form" onSubmit={handleSubmit} noValidate>
              <input className="lm-input" type="email" name="email" placeholder="your email" value={email} onChange={e=>setEmail(e.target.value)} autoComplete="email" aria-label="Your email" required disabled={busy}/>
              <button type="submit" className="lm-btn" disabled={busy}>{busy ? 'Sending...' : 'Send me the template'}</button>
              {state==='error' && <div className="lm-error">Something went wrong. Try again, or email claude@nuances.video.</div>}
            </form>
            <p className="lm-fine">Email used once, for the template.</p>
          </>
        )}
      </div>
    </section>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const nav = useCallback((target)=>navigate(target==='home' ? '/' : `/${target}`),[navigate]);
  const [rdy,setRdy] = useState(false);
  const heroAssetsReady = useDeferredMount();
  const [homeOpenId, setHomeOpenId] = useState(null);
  // Mobile hero is a curated, fade-from-black loop with its own charcoal poster.
  // Decide once at mount and render distinct <video> elements with their own keys so
  // the right source set + poster pair is what iOS Safari sees on first paint.
  const isMobileHero = typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches;
  useEffect(()=>{ const x=setTimeout(()=>setRdy(true),200); return()=>clearTimeout(x); },[]);
  // Hero videos ship with the `autoPlay` attribute as a harmless declarative hint,
  // but we don't rely on it alone: some deployments (observed on the deployed
  // Cloudflare Pages build) leave the element fully loaded (readyState 4) yet
  // paused, with no native retry. Once the element exists, wait for
  // 'canplaythrough' (enough buffered data that play() won't immediately
  // stall) and call play() ourselves, with a single retry on rejection. If
  // both attempts fail, do nothing further — the video's own poster attribute
  // is shown while paused, so it falls back to the same static frame as the
  // heroAssetsReady===false state.
  const heroVideoRef = useRef(null);
  useEffect(() => {
    const v = heroVideoRef.current;
    if (!v) return;
    let retried = false;
    const tryPlay = () => {
      v.play().catch(() => {
        if (retried) return;
        retried = true;
        v.play().catch(() => {});
      });
    };
    v.addEventListener('canplaythrough', tryPlay);
    return () => v.removeEventListener('canplaythrough', tryPlay);
  }, [heroAssetsReady, isMobileHero]);

  return (
    <main>
      <Seo
        title="nuances. a subtle accent."
        description="Boutique video production and communication strategy. Auckland based. Working across New Zealand, Australia, and France."
        path="/"
      />
      <section className="hero">
        <div className="hero-bg" style={{background:HERO_BG}}/>
        {heroAssetsReady ? (
          isMobileHero ? (
            <video key="hero-mobile" ref={heroVideoRef} className="hero-video" autoPlay muted loop playsInline preload="auto" poster="/05-Video/hero-mobile-poster.avif" aria-hidden="true">
              {/* mp4 first: iOS Safari can't decode VP9 webm, and h264 mp4 is smaller here (1.57 MB vs 2.75 MB). */}
              <source src="/05-Video/hero-mobile.mp4" type="video/mp4"/>
              <source src="/05-Video/hero-mobile.webm" type="video/webm"/>
            </video>
          ) : (
            <video key="hero-desktop" ref={heroVideoRef} className="hero-video" autoPlay muted loop playsInline preload="auto" poster="/05-Video/hero-poster.avif" aria-hidden="true">
              {/* mp4 first: matches mobile reasoning - same Safari VP9 quirk, h264 mp4 is smaller for this montage. */}
              <source src="/05-Video/hero.mp4" type="video/mp4"/>
              <source src="/05-Video/hero.webm" type="video/webm"/>
            </video>
          )
        ) : (
          isMobileHero ? (
            <div className="hero-video hero-video-placeholder" aria-hidden="true"/>
          ) : (
            <PictureImg src="/05-Video/hero-poster.jpg" alt="" className="hero-video" width={1920} height={1080} loading="eager" fetchPriority="high" aria-hidden="true" />
          )
        )}
        <div className="hero-grain" style={{opacity:GRAIN_OPACITY}}/>
        <div className={`hero-tagline-wrap${rdy?' vis':''}`}>
          <svg className="hero-tagline-brush" viewBox="0 0 520 130" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="brushG" x1="0" x2="1" y1="0.5" y2="0.5">
                <stop offset="0" stopColor="#B5838D" stopOpacity="0"/>
                <stop offset="0.08" stopColor="#B5838D" stopOpacity="0.55"/>
                <stop offset="0.5" stopColor="#B5838D" stopOpacity="0.92"/>
                <stop offset="0.92" stopColor="#B5838D" stopOpacity="0.6"/>
                <stop offset="1" stopColor="#B5838D" stopOpacity="0"/>
              </linearGradient>
            </defs>
            <path d="M 6 78 C 70 50, 160 52, 240 60 C 320 68, 410 60, 514 50 L 516 84 C 420 96, 320 92, 240 100 C 160 108, 80 102, 8 110 Z" fill="url(#brushG)"/>
          </svg>
          <p className="hero-tagline">A subtle accent.</p>
        </div>
      </section>
      <Statement/>
      <Definition/>
      <LeadMagnet/>
      <section className="hw">
        <div className="hw-hdr"><span className="lbl" style={{margin:0}}>Selected work</span><button className="hw-more" onClick={()=>nav('work')}>View all</button></div>
        <div className="hw-grid">
          {[
            { w: WORKS[0], cls: 'hw-wide' },
            { w: WORKS[1], cls: 'hw-sq' },
            { w: WORKS[2], cls: 'hw-sq' },
            { w: WORKS[3], cls: 'hw-wide' },
            { w: WORKS[4], cls: 'hw-sq' },
          ].map(({ w, cls }) => {
            const slug = WORK_TO_CASE_STUDY[w.id];
            return (
              <Tile key={w.id} p={w} cls={cls}
                isOpen={homeOpenId===w.id}
                onOpen={()=>setHomeOpenId(w.id)}
                onClose={()=>setHomeOpenId(null)}
                caseStudyBadge={!!slug}
                caseStudyCtaSlug={slug}/>
            );
          })}
        </div>
      </section>
      <LogoStrip/>
      <section className="how">
        <span className="lbl">How we work</span>
        <div className="how-grid">
          {[{n:'01',ti:'Strategy',b:'We begin with your audience, not your brief. Before a frame is shot, we ask what shift the work needs to cause, in belief, in behaviour, or in perception. The film follows from the answer.'},
            {n:'02',ti:'Story',b:'The strongest communication has a point of view. We find the angle that makes a viewer feel something they did not expect to feel, and build outward from there.'},
            {n:'03',ti:'Craft',b:'Every decision, light, edit rhythm, the choice of silence over music, is deliberate. We take the time the work requires.'}
          ].map((s,i)=>(
            <div key={s.n}>
              <span className="how-n">{s.n}</span><h3 className="how-title">{s.ti}</h3><p className="how-body">{s.b}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="hc">
        <h2 className="hc-h">let's talk</h2>
        <p className="hc-p">If there's a project, or even a half-formed idea, the shortest path is a conversation.</p>
        <button className="hc-btn" onClick={()=>nav('contact')}>Start a conversation</button>
        <a href="mailto:claude@nuances.video" className="hc-em">claude@nuances.video</a>
      </section>
    </main>
  );
};

const WORK_TO_CASE_STUDY = {
  'fnzcci-documentary': 'fnzcci',
  'debt-managers-testimonials': 'debtmanagers',
  'debt-managers-company-overview': 'debtmanagers',
};

const Work = () => {
  const [active,setActive] = useState('all');
  const [openId,setOpenId] = useState(null);
  const cats = [
    {label:'All', value:'all'},
    {label:'Corporate', value:'corporate'},
    {label:'Documentary', value:'documentary'},
    {label:'Testimonial', value:'testimonial'},
  ];
  const list = active==='all' ? WORKS : WORKS.filter(p=>p.cat===active);
  return (
    <main className="wp">
      <Seo
        title="Work | nuances"
        description="Selected video and documentary work from nuances. Brand films made for the person on the other side."
        path="/work"
      />
      <div className="wp-hdr"><h1 className="wp-title">Work</h1><div className="wp-filters">{cats.map(c=><button key={c.value} className={`wf${active===c.value?' on':''}`} onClick={()=>setActive(c.value)}>{c.label}</button>)}</div></div>
      <p className="wp-note">{ATTRIBUTION_NOTE}</p>
      <div className="wg">
        {list.map((p,i)=>{
          const slug = WORK_TO_CASE_STUDY[p.id];
          return (
            <div key={p.id} className={`gi-${p.span}`}>
              <Tile p={p} cls="gtile" isOpen={openId===p.id} onOpen={()=>setOpenId(p.id)} onClose={()=>setOpenId(null)} caseStudyBadge={!!slug} caseStudyCtaSlug={slug}/>
            </div>
          );
        })}
      </div>
    </main>
  );
};

const ServiceCTA = () => (
  <div className="service-cta">
    <p>If this is the kind of work you're thinking about, the shortest path is email. <a href="mailto:claude@nuances.video">claude@nuances.video</a>.</p>
    <p>If you'd rather think it through first, the <a href="/#leadmagnet">brief template</a> walks you through the questions we'd ask anyway.</p>
  </div>
);

const SERVICE_HASHES = {
  strategy: 0,
  internal: 1,
  recruitment: 2,
  brand: 3,
};

const Services = () => {
  const location = useLocation();
  const [openIndex, setOpenIndex] = useState(null);
  const [snapClosingIndex, setSnapClosingIndex] = useState(null);
  const panelRefs = useRef([]);
  const sectionRefs = useRef([]);

  // Open accordion based on hash on mount + on hash change
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && hash in SERVICE_HASHES) {
      const targetIndex = SERVICE_HASHES[hash];
      setOpenIndex(targetIndex);
      requestAnimationFrame(() => {
        sectionRefs.current[targetIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash]);

  // Measure each panel's natural height and store as --panel-h on its section.
  // scrollHeight on a max-height:0 overflow:hidden element returns the laid-out content height,
  // so we can measure without temporarily expanding.
  useEffect(() => {
    const measure = () => {
      panelRefs.current.forEach((panel, i) => {
        if (panel && sectionRefs.current[i]) {
          sectionRefs.current[i].style.setProperty('--panel-h', `${panel.scrollHeight}px`);
        }
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const toggle = (index) => {
    setOpenIndex(prev => {
      const next = prev === index ? null : index;

      // If switching from one open section to another, mark the
      // previously-open one to skip its transition this render.
      if (prev !== null && next !== null && prev !== index) {
        setSnapClosingIndex(prev);
      }

      if (next !== null) {
        requestAnimationFrame(() => {
          sectionRefs.current[next]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });
      }

      return next;
    });
  };

  useEffect(() => {
    if (snapClosingIndex !== null) {
      // Clear on next tick so subsequent close animations work normally.
      const id = requestAnimationFrame(() => {
        setSnapClosingIndex(null);
      });
      return () => cancelAnimationFrame(id);
    }
  }, [snapClosingIndex]);

  return (
    <main className="sp">
      <Seo
        title="Services | nuances"
        description="How we work. Video production and communication strategy, from first conversation to final cut."
        path="/services"
      />
      <section
        ref={el => sectionRefs.current[0] = el}
        id="strategy"
        className={`service-section${openIndex === 0 ? ' service-section-open' : ''}${snapClosingIndex === 0 ? ' service-section-snap' : ''}`}
        data-service="01"
      >
        <button
          type="button"
          className="service-header"
          aria-expanded={openIndex === 0}
          aria-controls="service-panel-0"
          onClick={() => toggle(0)}
        >
          <div className="service-eyebrow">01 / Communication strategy</div>
          <h2 className="service-headline">The half-day that decides what you should be making.</h2>
        </button>
        <div
          ref={el => panelRefs.current[0] = el}
          id="service-panel-0"
          className="service-panel"
          role="region"
        >
          <div className="service-block service-who">
            <h3 className="service-block-label">Who this is for</h3>
            <div className="service-block-body">
              <p>Leadership teams, comms directors, and founders who suspect the brief is the problem, not the production. The ones who have seen three studios quote them for three different videos and noticed that none of the quotes solved the underlying question.</p>
              <p>We work best with organisations where communication is the bottleneck, not the budget. New positioning that hasn't landed yet. Internal change that needs to be felt, not just announced. A market story that the existing assets aren't telling. If you already know what you want to make and just need a studio to make it, our other services are the better fit.</p>
            </div>
          </div>

          <div className="service-block service-problem">
            <h3 className="service-block-label">The problem we actually solve</h3>
            <div className="service-block-body">
              <p>Most communication briefs get written backwards. The brief starts with the deliverable, "we need a brand film," "we need a series of internal videos," "we need a recruitment campaign," and works back to the message. The result is a deliverable that's competent and a message that's blunted by the format.</p>
              <p>The work that needs to happen first is the work most studios skip, because most studios make their margin on production, not strategy. The question that gets glossed over is the one that matters most. What does this audience need to feel, believe, or do, and is video actually the right tool to move them.</p>
              <p>Sometimes the answer is yes. Then we know exactly what video to make. Sometimes the answer is no, the actual need is a written piece, a face-to-face conversation, a different kind of asset altogether. Either answer saves you months of production work on a deliverable that wouldn't have done the job.</p>
            </div>
          </div>

          <div className="service-block service-what">
            <h3 className="service-block-label">What we do</h3>
            <div className="service-block-body">
              <p>A half-day working session with you and the people who hold the message. Usually four hours, sometimes split across two mornings if that works better for your team.</p>
              <p>The session is structured around three questions. Who, specifically, is this for. What do they currently believe or feel, and what would have to change for the message to land. Is video actually the right tool, and if yes, what video. We work through them in order. By the end of the session we have either a clear film brief or a clear case for a different deliverable.</p>
              <p>A week later you receive a written brief document. Around eight to twelve pages. The document captures who the work is for, what it needs to say and not say, and what we'd actually recommend making. Often a specific film. Sometimes a different deliverable altogether. The document is yours to keep regardless of whether you go on to make the work with us.</p>
            </div>
          </div>

          <div className="service-block service-changes">
            <h3 className="service-block-label">What changes</h3>
            <div className="service-block-body">
              <p>Not "we have a video strategy now." What gets clearer:</p>
              <p>The leadership team agrees on what they're actually trying to communicate, in language they can all use. The communication that gets made next has a real audience, not a stakeholder list. The conversation about whether to invest in video stops being abstract and becomes specific. If you do go on to make the work, the brief is sharp enough that the studio you choose, us or someone else, can do their best work.</p>
            </div>
          </div>

          <div className="service-pullquote">
            <span className="service-pullquote-mark service-pullquote-mark-open" aria-hidden="true">“</span>
            <p className="service-pullquote-text">A surprising number of clients leave the session having decided not to make a video at all. That's a successful outcome.</p>
            <span className="service-pullquote-mark service-pullquote-mark-close" aria-hidden="true">”</span>
          </div>

          <div className="service-block service-proof">
            <h3 className="service-block-label">Proof</h3>
            <div className="service-block-body">
              <p>The strongest communication strategies share one feature. They start by being honest about what isn't working.</p>
              <p>Most internal comms isn't landing because the message is right but the format is wrong. Most brand communication isn't landing because the message is generic and the format is loud. Most recruitment communication isn't landing because the company is talking about itself instead of the candidate.</p>
              <p>A half-day of structured thinking, with someone who has been in this work for fifteen years and has no incentive to sell you a video you don't need, will usually surface where the real problem sits. From there, the next move becomes obvious. Sometimes that next move is film. Sometimes it is not. Either way, you stop spending budget on the wrong thing.</p>
            </div>
          </div>

          <ServiceCTA/>
        </div>
      </section>

      <section
        ref={el => sectionRefs.current[1] = el}
        id="internal"
        className={`service-section${openIndex === 1 ? ' service-section-open' : ''}${snapClosingIndex === 1 ? ' service-section-snap' : ''}`}
        data-service="02"
      >
        <button
          type="button"
          className="service-header"
          aria-expanded={openIndex === 1}
          aria-controls="service-panel-1"
          onClick={() => toggle(1)}
        >
          <div className="service-eyebrow">02 / Internal films</div>
          <h2 className="service-headline">Internal video your team will watch to the end.</h2>
        </button>
        <div
          ref={el => panelRefs.current[1] = el}
          id="service-panel-1"
          className="service-panel"
          role="region"
        >
          <div className="service-block service-who">
            <h3 className="service-block-label">Who this is for</h3>
            <div className="service-block-body">
              <p>Heads of internal communications, HR leads, and chief people officers in organisations between 200 and 5,000 staff. The ones who've watched the all-staff town hall recording drop off after ninety seconds. The ones whose engagement survey scores keep saying "we don't hear from leadership often enough" and whose response to that has produced more emails that nobody opens.</p>
              <p>We work best with organisations large enough that internal communication has become a real problem to solve, small enough that we can know your team and your context properly. If you're elsewhere on that spectrum, we should still talk. The conversation will help us both decide whether we're the right fit.</p>
            </div>
          </div>

          <div className="service-block service-problem">
            <h3 className="service-block-label">The problem we actually solve</h3>
            <div className="service-block-body">
              <p>Most internal video gets made the wrong way. Someone in leadership has a message. The message gets briefed to a video team. The video team makes a competent piece. The piece goes out. The metrics come back muted. Everyone agrees it was professional and moves on.</p>
              <p>The problem isn't the production. The problem is that the brief skipped the question that matters most: what does the person watching this need to feel before they'll do anything different on Monday morning?</p>
              <p>When briefs start with logistics, length, deadline, who's on camera, you get videos that document a decision. When briefs start with the person on the other side, you get videos that change one.</p>
              <p>We work with you on the brief first. Sometimes that means we recommend not making the video at all, because the actual need is a face-to-face conversation or a written piece the leadership team hasn't yet been brave enough to put their name to. When the answer is "yes, a film is the right tool," the work that follows has a job.</p>
            </div>
          </div>

          <div className="service-block service-what">
            <h3 className="service-block-label">What we do</h3>
            <div className="service-block-body">
              <p>Three stages, roughly:</p>
              <p><strong>Foundation.</strong> A working session with you and whoever owns the message. Usually two hours. We map the person on the other side. What they currently believe. What they need to believe. What would have to be true in their week for them to act on what they've watched. By the end of the session, the brief is either sharper or abandoned. Either is progress.</p>
              <p><strong>Production.</strong> Small crew, mostly two of us in the room. We don't use the autocue chair you've seen at conferences. We shoot interviews as conversations, often unscripted, in spaces that hold the speaker's actual context. The footage we get this way is the footage that earns attention.</p>
              <p><strong>Edit and delivery.</strong> We cut to the brief, not to the footage. The first version is rarely the final one. We expect one to two rounds of revisions and price accordingly. Final delivery is in the formats your distribution actually needs, with thumbnails and a one-page brief for the rollout team.</p>
            </div>
          </div>

          <div className="service-block service-changes">
            <h3 className="service-block-label">What changes</h3>
            <div className="service-block-body">
              <p>Not "engagement rate goes up by X percent." Things that actually shift:</p>
              <p>A senior leader's all-staff message gets forwarded by direct reports to their teams, unprompted. A staff Q&amp;A produces twice the questions it used to. An engagement survey's "I feel informed about company direction" score moves three points and stays there. Someone you didn't know watched the film stops you in the corridor.</p>
              <p>We're cautious about big numbers because the systems most organisations use to measure internal video are blunt. We'd rather agree on two or three specific signals at the start of the project and check honestly at the end.</p>
            </div>
          </div>

          <div className="service-pullquote">
            <span className="service-pullquote-mark service-pullquote-mark-open" aria-hidden="true">“</span>
            <p className="service-pullquote-text">Internal film works when it's made for the person watching, not the person sending it.</p>
            <span className="service-pullquote-mark service-pullquote-mark-close" aria-hidden="true">”</span>
          </div>

          <div className="service-block service-proof">
            <h3 className="service-block-label">Proof</h3>
            <div className="service-block-body">
              <p>In 2024, Claude produced a four-part series for DebtManagers while Co-Founder at MindShift Films.</p>
              <p>What the brief did not say, but what the films were built around, was the small physical thing every person in the films had in common. They had picked up a phone. The series became, in the cut, a meditation on that act.</p>
              <blockquote className="service-quote">
                "Claude understood what we were trying to say better than we did when we briefed him."
                <cite className="service-quote-cite">Mark Francis, DebtManagers</cite>
              </blockquote>
            </div>
          </div>

          <ServiceCTA/>
        </div>
      </section>

      <section
        ref={el => sectionRefs.current[2] = el}
        id="recruitment"
        className={`service-section${openIndex === 2 ? ' service-section-open' : ''}${snapClosingIndex === 2 ? ' service-section-snap' : ''}`}
        data-service="03"
      >
        <button
          type="button"
          className="service-header"
          aria-expanded={openIndex === 2}
          aria-controls="service-panel-2"
          onClick={() => toggle(2)}
        >
          <div className="service-eyebrow">03 / Recruitment</div>
          <h2 className="service-headline">The film a good candidate decides on.</h2>
        </button>
        <div
          ref={el => panelRefs.current[2] = el}
          id="service-panel-2"
          className="service-panel"
          role="region"
        >
          <div className="service-block service-who">
            <h3 className="service-block-label">Who this is for</h3>
            <div className="service-block-body">
              <p>Talent leads and hiring managers in organisations where the candidate experience starts before the first interview. The ones competing for engineers, designers, or specialists who have options. The ones who've watched their LinkedIn ads underperform and noticed that the strongest candidates do their research before they ever apply.</p>
              <p>We work best with organisations hiring for roles where the candidate's decision matters more than the volume of applications. If you're recruiting at scale and need pipeline films that play across hundreds of job ads, we should still talk, but you may need a different kind of partner.</p>
            </div>
          </div>

          <div className="service-block service-problem">
            <h3 className="service-block-label">The problem we actually solve</h3>
            <div className="service-block-body">
              <p>Most recruitment films are made for the wrong audience. They get briefed by HR, approved by leadership, and signed off by a brand team that wants the company to look good. The candidate, the one the film is supposedly for, is not in the room.</p>
              <p>The result is the recruitment film you've seen a thousand times. Drone shot of the office. Smiling team in a glass meeting room. Someone in a leadership role saying "we're a people company." A candidate watches forty seconds, learns nothing about what their actual day would look like, and clicks away.</p>
              <p>A film that helps you hire doesn't sell the company. It tells the truth about the job, with enough texture that the right candidate recognises themselves and the wrong one self-selects out. The candidate who turns down your offer was never going to be a good hire. The candidate who applies because the film showed them something true is the one who stays.</p>
              <p>We work with you on the brief first. What is the actual experience of doing this job at your company. What kind of person thrives. What kind of person leaves. What would the strongest candidate need to see to choose you over an offer from somewhere with a bigger budget.</p>
            </div>
          </div>

          <div className="service-block service-what">
            <h3 className="service-block-label">What we do</h3>
            <div className="service-block-body">
              <p>Three stages, roughly:</p>
              <p><strong>Foundation.</strong> A working session with you and one or two people who currently do the role you're hiring for. Usually two hours. Most of it is the existing team talking about their work honestly, not to camera, just to us. By the end of the session we know what makes the role specific, what gets glossed over in job descriptions, and what a strong candidate would need to see to take you seriously.</p>
              <p><strong>Production.</strong> Two of us in the room, no larger crew. We interview the people who actually do the work, not the people who manage them. We shoot in the spaces where the work happens, not in the meeting room you book for visitors. We don't write scripts and we don't run through "key messages." The footage we get this way is the footage candidates trust.</p>
              <p><strong>Edit and delivery.</strong> We cut to the candidate's question, not to the company's positioning. The first version is rarely the final one. We expect one to two rounds of revisions and price accordingly. Final delivery in the formats your application flow actually needs, with versions sized for the platforms where candidates first encounter you.</p>
            </div>
          </div>

          <div className="service-block service-changes">
            <h3 className="service-block-label">What changes</h3>
            <div className="service-block-body">
              <p>Not "application volume goes up by X percent." If anything, the right recruitment film often makes application volume go down and quality go up. The signals we'd watch for:</p>
              <p>The interview pool gets smaller but the offer-acceptance rate goes up. The hiring manager stops asking you to fix the brief. Someone you didn't approach reaches out because they watched the film and wanted to work with the person on screen.</p>
              <p>We're cautious about big recruitment marketing numbers because the systems most organisations use to measure candidate response are blunt. We'd rather agree at the start what would count as success for the specific role and check honestly at the end.</p>
            </div>
          </div>

          <div className="service-pullquote">
            <span className="service-pullquote-mark service-pullquote-mark-open" aria-hidden="true">“</span>
            <p className="service-pullquote-text">The candidate who self-selects out was never going to stay. The candidate who self-selects in has already started joining your team in their head.</p>
            <span className="service-pullquote-mark service-pullquote-mark-close" aria-hidden="true">”</span>
          </div>

          <div className="service-block service-proof">
            <h3 className="service-block-label">Proof</h3>
            <div className="service-block-body">
              <p>A recruitment film works when the candidate who watches it sees themselves in it. Not the company. Not the leadership team. The person who would do the job.</p>
              <p>This means the film has to do something most recruitment films refuse to do. It has to be honest about what's hard. The strongest engineers, the strongest specialists, the people you most want to hire, have all worked at companies that promised them culture and delivered chaos. They have learned to distrust the smiling-team-in-glass-meeting-room genre.</p>
              <p>A film that tells them the truth about your tradeoffs, the parts of the job that are hard, the kind of person who struggles here, builds more trust than any number of culture statements.</p>
              <p>Our approach to recruitment work is the same as our approach to internal films. We do the work that earns attention rather than the work that asks for it.</p>
            </div>
          </div>

          <ServiceCTA/>
        </div>
      </section>

      <section
        ref={el => sectionRefs.current[3] = el}
        id="brand"
        className={`service-section${openIndex === 3 ? ' service-section-open' : ''}${snapClosingIndex === 3 ? ' service-section-snap' : ''}`}
        data-service="04"
      >
        <button
          type="button"
          className="service-header"
          aria-expanded={openIndex === 3}
          aria-controls="service-panel-3"
          onClick={() => toggle(3)}
        >
          <div className="service-eyebrow">04 / Brand films</div>
          <h2 className="service-headline">A film with an argument.</h2>
        </button>
        <div
          ref={el => panelRefs.current[3] = el}
          id="service-panel-3"
          className="service-panel"
          role="region"
        >
          <div className="service-block service-who">
            <h3 className="service-block-label">Who this is for</h3>
            <div className="service-block-body">
              <p>Founders, CEOs, and marketing leads at organisations in a moment of transition. A rebrand. A new positioning. A pivot the market hasn't quite registered yet. The ones who have a story to tell and have realised the story isn't fitting in the formats they've been using.</p>
              <p>We work best with organisations where the brand film is part of a larger communication strategy, not an isolated asset. If you need a single hero film for a campaign you've already designed, we can do that, but we're more useful when we're brought in earlier, when the brand story itself is still being shaped.</p>
            </div>
          </div>

          <div className="service-block service-problem">
            <h3 className="service-block-label">The problem we actually solve</h3>
            <div className="service-block-body">
              <p>Most brand films get briefed at the wrong altitude. The brief says "tell our story." The studio interprets that as "make us look good." Two months later, a beautifully shot, professionally scored, three-minute film exists that says nothing the audience didn't already assume. It plays at the leadership offsite. It sits on the company's About page. Nobody watches it twice.</p>
              <p>What a brand film can do, when the brief gets out of the way, is take a point of view that already exists, often buried inside the leadership team's heads, and make it visible. It can compress a year of strategic positioning into ninety seconds that the audience actually remembers. It can give your team something to point to when they're trying to explain what makes you different to a new hire or a prospective client.</p>
            </div>
          </div>

          <div className="service-block service-what">
            <h3 className="service-block-label">What we do</h3>
            <div className="service-block-body">
              <p>Three stages, roughly:</p>
              <p><strong>Foundation.</strong> A working session with the leadership team or whoever holds the point of view. Usually two to three hours, longer than our other services because brand work needs to surface what's currently implicit. We're listening for the moment when someone says something true that wasn't in the brand deck. That moment becomes the spine of the film.</p>
              <p><strong>Production.</strong> Approach depends on what the point of view requires. Sometimes that's a single founder interview, considered and unhurried. Sometimes it's a series of vignettes across the business. Sometimes it's an essay film with voiceover and no on-camera talent at all. We choose the form after we understand the message, not before. Small crew either way, mostly two of us in the room.</p>
              <p><strong>Edit and delivery.</strong> Brand films take longer to cut than our other work because the rhythm matters more. A film that's strategic at ninety seconds becomes generic at sixty and indulgent at three minutes. We expect two to three rounds of revisions on brand work, more than our other services, and price accordingly. Final delivery in the formats your distribution needs, plus a director's cut you can keep for the leadership archive.</p>
            </div>
          </div>

          <div className="service-block service-changes">
            <h3 className="service-block-label">What changes</h3>
            <div className="service-block-body">
              <p>Not "brand awareness." What you'd notice instead:</p>
              <p>A sales conversation that used to take three meetings closes in two because the prospect watched the film first. A new hire references the film in their first week, unprompted, as a reason they joined. An investor pitch lands on a different note than it would have, because the film does the work the slides can't. Someone in the leadership team sends the film to a peer at another company and says "this is what we're trying to do."</p>
              <p>The strongest brand films don't change anyone's mind. They give people who already half-believe in you the language and the imagery to commit. That's the work, and that's what we measure.</p>
            </div>
          </div>

          <div className="service-pullquote">
            <span className="service-pullquote-mark service-pullquote-mark-open" aria-hidden="true">“</span>
            <p className="service-pullquote-text">A film that makes your sector's biggest competitor wince a little is a film that your right customers will recognise as theirs.</p>
            <span className="service-pullquote-mark service-pullquote-mark-close" aria-hidden="true">”</span>
          </div>

          <div className="service-block service-proof">
            <h3 className="service-block-label">Proof</h3>
            <div className="service-block-body">
              <p>The strongest brand films do one thing that most don't. They commit to a point of view sharp enough to alienate someone.</p>
              <p>A film that everyone in your market would nod along to is a film that none of them will remember.</p>
              <p>This is the conversation we have at the start of every brand project. What are you willing to say that your competitors won't. What is true about your work that the polite version of your website can't admit. The brand film is the place to say it, with the production value that gives it weight.</p>
              <p>Our approach to brand work follows from this. We're not the studio for the brand film that needs to please every stakeholder. We're the studio for the brand film that needs to mean something.</p>
            </div>
          </div>

          <ServiceCTA/>
        </div>
      </section>

      <div className="services-close">
        <div className="services-closing-flourish">fin.</div>
      </div>
    </main>
  );
};

const About = () => {
  return(
    <main className="ap">
      <Seo
        title="About | nuances"
        description="A French-Kiwi video studio in Auckland. We make work that's felt, not told."
        path="/about"
      />
      <section className="about-studio">
        <div className="about-title-row">
          <h1 className="about-title">About.</h1>
          <div className="about-eyebrow">The studio</div>
        </div>
        <p className="about-studio-body">Auckland-based, working across New Zealand, Australia, and France. Small crew. We work with comms managers, HR leads, and CEOs for whom video is part of the strategy rather than a budget line.</p>
      </section>
      <hr className="about-divider"/>
      <section className="about-founder">
        <div className="about-portrait">
          <PictureImg src="/about/claude-portrait.jpg" alt="Claude, Founder" width={1024} height={1024} sizes="(min-width: 900px) 360px, 100vw" loading="lazy"/>
        </div>
        <div className="about-founder-note">
          <div className="about-eyebrow">A note from Claude, Founder & Director</div>
          <p className="lede">I spent fifteen years as a business consultant watching the same thing happen. A team would leave a training day with pages of notes and the best intentions. Two weeks later, almost none of it had moved. Not because the content was wrong. Because knowing something and feeling its relevance are two different things entirely.</p>
          <p>That gap is why nuances exists. The brief is never just the video. It is always the person on the other side: what they need to feel, what would make them stop, what would make them act.</p>
          <p>Years of behaviour-change work taught me that the pause, the choice of words, the moment of silence, are not aesthetic decisions. They are strategic ones.</p>
        </div>
      </section>
      <Showreel context="about"/>
      <p className="about-closing">Auckland-based. Available wherever the work calls for it.</p>
    </main>
  );
};

const Contact = () => {
  const [form,setForm]=useState({name:'',company:'',email:'',msg:''});
  const [state,setState]=useState('idle');
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const handleSubmit = async e => {
    e.preventDefault();
    if(!(form.name && form.company && form.email && form.msg)) return;
    setState('submitting');
    try {
      const fd = new FormData(e.target);
      const r = await fetch('https://formspree.io/f/xjglqero', {method:'POST', body:fd, headers:{Accept:'application/json'}});
      if(r.ok){
        setState('success');
        setForm({name:'',company:'',email:'',msg:''});
      } else {
        setState('error');
      }
    } catch(err){
      setState('error');
    }
  };
  const busy = state==='submitting';
  return(
    <main className="cp">
      <Seo
        title="Contact | nuances"
        description="Start a conversation. Auckland-based, working across New Zealand, Australia, and France."
        path="/contact"
      />
      <div className="cp-inner">
        <div className="contact-intro">
          <p>The shortest path is email. <a href="mailto:claude@nuances.video">claude@nuances.video</a>.</p>
          <p>If you'd rather, the form below works too. Tell us what you're working on, in as much or as little detail as feels right.</p>
        </div>
      </div>
      <Showreel context="contact"/>
      <div className="cp-inner">
      <span className="cp-rule"/>
      <div>{state==='success' ? (
        <p className="contact-confirmation">Thanks. We'll be in touch.</p>
      ) : (
        <form action="https://formspree.io/f/xjglqero" method="POST" className="cf contact-form" onSubmit={handleSubmit}>
          <div className="ff field"><label className="fl" htmlFor="name">Name</label><input id="name" name="name" type="text" className="fi" value={form.name} onChange={e=>set('name',e.target.value)} autoComplete="name" required disabled={busy}/></div>
          <div className="ff field"><label className="fl" htmlFor="company">Company</label><input id="company" name="company" type="text" className="fi" value={form.company} onChange={e=>set('company',e.target.value)} autoComplete="organization" required disabled={busy}/></div>
          <div className="ff field"><label className="fl" htmlFor="email">Email</label><input id="email" name="email" type="email" className="fi" value={form.email} onChange={e=>set('email',e.target.value)} autoComplete="email" required disabled={busy}/></div>
          <div className="ff field"><label className="fl" htmlFor="message">What you're working on</label><textarea id="message" name="message" className="fi" style={{resize:'none'}} rows={6} value={form.msg} onChange={e=>set('msg',e.target.value)} required disabled={busy}/></div>
          <button type="submit" className="fs contact-submit" disabled={busy}>{busy ? 'Sending...' : 'Send'}</button>
          {state==='error' && <div className="contact-error">Something went wrong. Try again, or email claude@nuances.video directly.</div>}
        </form>
      )}</div>
    </div></main>
  );
};

/* Case study page */

const renderRichText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
};

const CaseStudyHero = ({hero}) => (
  <section className="cs-hero">
    <div className="cs-hero-image">
      <PictureImg
        src={hero.image}
        alt={hero.alt || ''}
        width={hero.width}
        height={hero.height}
        loading="eager"
        fetchPriority="high"
      />
    </div>
    <div className="cs-hero-overlay">
      <div className="cs-hero-meta">
        <div className="cs-hero-client">{hero.client}</div>
        <div className="cs-hero-detail">
          <span className="cs-hero-year">{hero.year}</span>
          <span className="cs-hero-sep">·</span>
          <span className="cs-hero-format">{hero.format}</span>
        </div>
      </div>
    </div>
  </section>
);

const CaseStudyCarouselItem = ({item, isActive, onActivate}) => (
  <div className={`cs-carousel-item${isActive ? ' active' : ''}`}>
    {isActive ? (
      <div className="cs-carousel-player">
        <iframe
          src={`https://player.vimeo.com/video/${item.vimeoId}?autoplay=1&title=0&byline=0&portrait=0&dnt=1`}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={`${item.name}, DebtManagers testimonial`}
        />
      </div>
    ) : (
      <button
        type="button"
        className="cs-carousel-thumb"
        onClick={onActivate}
        aria-label={`Play ${item.name}'s testimonial`}
      >
        <PictureImg src={item.thumbnail} alt={item.name} width={1200} height={675} loading="lazy"/>
        <div className="cs-carousel-thumb-overlay">
          <span className="cs-carousel-thumb-play" aria-hidden="true">&#9654;</span>
          <span className="cs-carousel-thumb-name">{item.name}</span>
        </div>
      </button>
    )}
  </div>
);

const CaseStudyCarousel = ({items}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  return (
    <div className="cs-carousel-wrap">
      <div className="cs-carousel">
        {items.map((item, idx) => (
          <CaseStudyCarouselItem
            key={idx}
            item={item}
            isActive={activeIndex === idx}
            onActivate={() => setActiveIndex(idx)}
          />
        ))}
      </div>
    </div>
  );
};

const CaseStudyContent = ({content}) => (
  <>
    {content.map((item, i) => {
      if (item.type === 'paragraph') {
        return <p key={i}>{renderRichText(item.text)}</p>;
      }
      if (item.type === 'image-break') {
        return (
          <figure key={i} className="cs-image-break">
            <PictureImg src={item.src} alt={item.alt} width={item.width} height={item.height} loading="lazy"/>
          </figure>
        );
      }
      if (item.type === 'vimeo') {
        if (item.vimeoId) {
          return (
            <div key={i} className="cs-video-embed">
              <VimeoFacade vimeoId={item.vimeoId} title={item.caption || 'Video'} poster={item.poster}/>
            </div>
          );
        }
        return (
          <div key={i} className="cs-video-placeholder" aria-label={`Video placeholder: ${item.caption || ''}`}>
            <span className="cs-video-placeholder-label">Video embed: {item.caption || 'TBD'}</span>
          </div>
        );
      }
      if (item.type === 'carousel') {
        return <CaseStudyCarousel key={i} items={item.items}/>;
      }
      return null;
    })}
  </>
);

const CaseStudySection = ({section}) => (
  <section className="cs-section">
    <div className="cs-section-eyebrow">{section.label}</div>
    <div className="cs-section-content">
      <CaseStudyContent content={section.content}/>
      {section.testimonial && (
        <blockquote className="service-quote">
          {section.testimonial.quote}
          <cite className="service-quote-cite">{section.testimonial.cite}</cite>
        </blockquote>
      )}
    </div>
  </section>
);

const CaseStudyPullQuote = ({text}) => (
  <div className="service-pullquote cs-pullquote-spacing">
    <span className="service-pullquote-mark service-pullquote-mark-open" aria-hidden="true">&ldquo;</span>
    <p className="service-pullquote-text">{text}</p>
    <span className="service-pullquote-mark service-pullquote-mark-close" aria-hidden="true">&rdquo;</span>
  </div>
);

const CaseStudyRelated = ({current}) => {
  const cs = caseStudies[current];
  if (!cs) return null;
  const others = cs.related.map(slug => caseStudies[slug]).filter(Boolean);
  return (
    <section className="cs-related">
      <div className="cs-related-label">More</div>
      <div className="cs-related-grid">
        {others.map(other => (
          <Link key={other.slug} to={`/work/${other.slug}`} className="cs-related-tile">
            <div className="cs-related-image">
              <PictureImg src={other.hero.image} alt="" width={other.hero.width} height={other.hero.height} loading="lazy"/>
            </div>
            <div className="cs-related-title">{other.title}</div>
          </Link>
        ))}
        <Link to="/work" className="cs-related-tile cs-related-tile-allwork">
          <div className="aw-mosaic" aria-hidden="true">
            <img src="/05-Video/work/vinci-go-north-poster.avif" alt="" loading="lazy"/>
            <img src="/05-Video/work/fnzcci-documentary-poster.avif" alt="" loading="lazy"/>
            <img src="/05-Video/work/debt-managers-testimonials-poster.avif" alt="" loading="lazy"/>
            <img src="/05-Video/work/debt-managers-company-overview-poster.avif" alt="" loading="lazy"/>
          </div>
          <div className="aw-scrim" aria-hidden="true"/>
          <div className="cs-related-title">All work</div>
        </Link>
      </div>
    </section>
  );
};

const NotFound = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  return (
    <main className="nf-page">
      <Seo
        title="Not found | nuances"
        description="That page could not be found."
        path={path}
        noindex
      />
      <section className="nf-panel">
        <div className="nf-eyebrow">404</div>
        <h1 className="nf-title">That page slipped out of frame.</h1>
        <p className="nf-body">It might have moved, or the link might have been mistyped. <Link to="/" className="nf-link">Back to the home page</Link>.</p>
      </section>
    </main>
  );
};

const CaseStudyPage = () => {
  const {slug} = useParams();
  const cs = caseStudies[slug];
  if (!cs) {
    return <NotFound/>;
  }
  const seo = CASE_STUDY_SEO[slug];
  return (
    <main className="cs-page">
      {seo && (
        <Seo
          title={seo.title}
          description={seo.description}
          path={`/work/${slug}`}
        />
      )}
      <CaseStudyHero hero={cs.hero}/>
      {cs.sections.map((section, i) => {
        if (section.type === 'pull-quote') {
          return <CaseStudyPullQuote key={i} text={section.text}/>;
        }
        return <CaseStudySection key={i} section={section}/>;
      })}
      <CaseStudyRelated current={cs.slug}/>
    </main>
  );
};

/* Router shell with cross-fade transition */

const App = () => {
  const location = useLocation();
  const [displayed, setDisplayed] = useState(location);
  const [out, setOut] = useState(false);

  useEffect(() => {
    if (location.pathname === displayed.pathname && location.search === displayed.search) return;
    setOut(true);
    const t = setTimeout(() => {
      setDisplayed(location);
      window.scrollTo({top:0, behavior:'instant'});
      setOut(false);
    }, 320);
    return () => clearTimeout(t);
  }, [location, displayed.pathname, displayed.search]);

  return (
    <>
      <Nav displayedPath={displayed.pathname}/>
      <div className={`page-wrap ${out?'page-out':'page-in'}`}>
        <Routes location={displayed}>
          <Route path="/" element={<Home/>}/>
          <Route path="/work" element={<Work/>}/>
          <Route path="/work/fnzcci-documentary" element={<Navigate replace to="/work/fnzcci"/>}/>
          <Route path="/work/:slug" element={<CaseStudyPage/>}/>
          <Route path="/services" element={<Services/>}/>
          <Route path="/about" element={<About/>}/>
          <Route path="/contact" element={<Contact/>}/>
          <Route path="*" element={<NotFound/>}/>
        </Routes>
        <Footer/>
      </div>
      <div className={`wash${out?' wash-on':''}`} aria-hidden="true"/>
    </>
  );
};

export default App;
